<template>
  <tile class="opc-pbx opc-pbx-regional">
    <foldable v-model="isExpanded" :title="this.$t('billing.planform.title.zoomphone')">
      <div slot="head">
        <div class="opc-pbx-head">
          <img class="opc-pbx-regional__logo-icon" :src="ZoomPhoneIcon" :alt="$t('billing.planform.title.zoomphone')" />
          <div class="opc-pbx-head__logo">
            <div class="opc-pbx-regional__logo">
              Zoom Phone
              <i v-if="!isExpanded" class="zm-icon-success opc-pbx__checked-icon"></i>
            </div>
            <zm-button data-testid="checkout-button-203" class="opc-pbx__link" type="link" v-if="isExpanded" @click="showPlanDetailDialog">
              {{ $t('billing.plan_details') }}
            </zm-button>
          </div>
        </div>
      </div>
      <div slot="collapse" class="opc-config__collapse">
        <div v-for="selectedPlansLabel in selectedPlansLabelList" :key="selectedPlansLabel">
          {{ selectedPlansLabel }}
        </div>
        <div>{{ callingFeesLabel }}</div>
      </div>
      <div class="opc-pbx__main">
        <!--What calling plans do your users need.-->
        <p class="label-title-1">
        <span id="label-title-1">{{ $t('billing.pbx.what_calling_plans') }}</span>
        <zm-tooltip
          v-model='callingPlanToolTip'
          slot="label-suffix"
          placement="top"
          :content="$t('billing.pbx.calling_plan_selected_tips')"
        >
          <zm-button data-testid="checkout-button-204" :aria-label="'Learn More about ' + $t('billing.pbx.what_calling_plans')" type="icon" icon="zm-icon-info-outline main-color-icon" ghost size="mini"></zm-button>
        </zm-tooltip>
      </p>
       <zm-radio-group aria-labelledby="label-title-1" class="opc-tag-radio" name="calling-plans-group" v-model="activeRegionKey">
          <zm-radio data-testid="checkout-radio-205" v-for="region in regionsList" :key="region.key" :label="region.key" class="opc-tag-radio__option--checked">
            <span v-html="getCallingPlanLabelForSelect(region)"></span>
          </zm-radio>
        </zm-radio-group>
        <zm-alert v-if="totalLimitedPlansCount > maxLimitedPlansCount" class="number-item" type="warning" :closable="false">
          <div class="number-tips-info" ref="numberTipsRef">
            <i class="billing-icon-warning" />
            <p v-html="$t('billing.pbx.more_than_numbers', [maxLimitedPlansCount, pbxContactSalesLink])"></p>
          </div>
        </zm-alert>
       <div :key="activeRegionKey+','+selectedPbxBillingCycle">
          <config-section :show-head="!!regionsConfig[activeRegionKey].callings.length" :show-action="!testRegionalCallingPlansFull(selectedPbxBillingCycle)">
            <span slot="title">{{ $t('billing.pbx.calling_plans') }}</span>
            <span slot="description">{{ callingPlansDesc }}</span>
            <number-select
              ref="callingSelect"
              :data-tracking="dataTrackingCallingPlan"
              :optionList="mapSelectOptions(globalSelectCallingPlans, selectedPbxBillingCycle)"
              :deletable="callingPlanDeletable"
              :before-change="beforeChangeCallingPlan"
              :title="$t('billing.pbx.calling_plans')"
              v-model="regionsConfig[activeRegionKey].callings"
            />
            <zm-button data-testid="checkout-button-206" slot="action" type="link" @click="addAnotherCallingPlan">
              {{ $t('billing.pbx.add_another_calling_plan') }}
            </zm-button>
          </config-section>
           <config-section :show-head="!!regionsConfig[activeRegionKey].additions.length" :show-action="!testRegionalAdditionalPlansFull(selectedPbxBillingCycle)">
            <span slot="title">{{ $t('billing.pbx.additional_numbers') }}</span>
            <span slot="description">
              {{ $t('billing.pbx.additional_numbers_desc') }}
            </span>
            <number-select
              ref="tollfreeSelect"
              :data-tracking="dataTrackingNumbers"
              :optionList="mapSelectOptions(globalSelectAdditionalPlans, selectedPbxBillingCycle)"
              :title="$t('billing.pbx.additional_numbers')"
              v-model="regionsConfig[activeRegionKey].additions"
            />
            <zm-tooltip
              v-model='additionalNumbersToolTip'
              slot="action"
              placement="top"
              :disabled="regionsConfig[activeRegionKey].additions.length > 0"
              :content="$t('billing.pbx.add_additional_numbers_tips')"
            >
              <zm-button data-testid="checkout-button-207" type="link" @click="addAdditionalNumbers">
                {{ $t('billing.pbx.add_additional_numbers') }}
              </zm-button>
            </zm-tooltip>
          </config-section>
        </div>
        <opcCardCascade v-model="billingCycleModel" divide-line :tracking-data='planAndCycleTrackingData' :config="cycleAndPlansConfig" @change="switchPlansCycle">
          <div slot="group1-title">
            <span id="group1-title">{{ callingFeeLabel }}</span>
            <zm-tooltip v-model='callingfeesToolTip' slot="label-suffix" placement="top">
              <p v-html="$t('billing.pbx.calling_fee_tips', [activeRegionLabel])" slot="content"></p>
              <zm-button data-testid="checkout-button-208" :aria-label="'Learn More about ' + callingFeeLabel" type="icon" icon="zm-icon-info-outline main-color-icon" ghost size="mini"></zm-button>
            </zm-tooltip>
          </div>
        </opcCardCascade>
        <ZP0ABJ v-if='isVisible0ABJValidate' />
        <appeal-byoc v-else />
      </div>
    </foldable>
  </tile>
</template>

<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import OpcCardCascade from '../component/opc-card-cascade';
import ZP0ABJ from '../component/0ABJ.vue';
import AppealByoc from '../component/appeal-byoc.vue';
import ZoomPhoneIcon from '@/assets/plans/logo_pbx.svg';
import {
  computedPbxBasePlans,
  computedPbxCallingPlans,
  computedPbxPlans,
  computedPbxTollFreePlans,
  computedPbxPhoneNumberPlans,
  PBX_SUB_TYPES
} from '@/common/service/computers/plans/pbx';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { BILLING_CYCLE } from '@/common/constants';
import { groupBy } from 'lodash';
import { PBX_REGIONS,pbxGlobalSelect, PBX_GS } from '@/plans/ZOOM_PHONE/helper/constants';
import plansMixin from '@/plans/mx/plan';
import ConfigSection from '../component/config-section.vue';
import NumberSelect from '../component/select-number-list.vue';
import mxEdit from './mx-edit';
import mxGS from './mx-gs';
import merge from 'deepmerge'
import _, { set as _set } from 'lodash';

import {
  trackingAddAdditionalNumbers,
  trackingAddAnotherCallingPlan,
  trackingSelectCallingPlan,
  getZPCallingFeesText
} from '@/plans/ZOOM_PHONE/helper/utils';
import {planTypes} from '@/common/constants';
import { CURRENCY_COOKIE_KEY } from '@/utils/currency';

export default {
  key: planTypes[planTypes.ZOOM_PHONE],
  data() {
    return {
      callingPlanToolTip: false,
      callingfeesToolTip: false,
      additionalNumbersToolTip: false,
      ZoomPhoneIcon,
      isExpanded: true,
      activeRegionKey: 'GS',
      form: {
        category: '',
        localNumber: 0,
        tollFreeNumber: 0,
      },
    };
  },
  components: {
    Foldable,
    Tile,
    OpcCardCascade,
    ConfigSection,
    NumberSelect,
    ZP0ABJ,
    AppealByoc
  },
  created() {
    this.initPbxForm().then(() => this.numberListLoading = false);
  },
  mixins: [plansMixin, mxEdit, mxGS],
  props: {
    region: {
      // show top-right drop down arrow
      type: String,
      default: 'JP',
    },
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
      japanIP: (state) => state.Common.japanIP,
    }),
    ...mapGetters(['pbxBaseInfo']),
    isVisible0ABJValidate() {
      // japan currency or japan location
      if (!this.$config('enable0ABJTool')) {
        return false;
      }
      const currency = this.$cookie.get(CURRENCY_COOKIE_KEY);
      if (currency === "JPY" || this.japanIP) {
        return true;
      }
      return false;
    },
    pbxBillingCycle: {
      get() {
        return this.pbxBaseInfo.billingCycle;
      },
      set(newValue) {
        this.setPbxBillingCycle(newValue);
      }
    },
    pbxBasePlanCode: {
      get() {
        return this.pbxBaseInfo.basePlanCode;
      },
      set(newValue) {
        this.setPbxBasePlanCode(newValue);
      }
    },
    billingCycleModel: {
      get() {
        return [this.pbxBaseInfo.billingCycle, this.pbxBaseInfo.basePlanCode];
      },
      set(val) {
        const [billingCycle, usageFeePlanCode] = val
        this.setPbxBillingCycle(billingCycle);
        this.setPbxBasePlanCode(usageFeePlanCode);
      }
    },
    basePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    cycleAndPlansConfig() {
      const { annual, monthly, payAsYouGo } = groupBy(this.basePlans, 'billing_cycle');
      return {
        group0: {
          title: this.$t('billing.select_billing_cycle'),
        },
        group1: {
          slot: 'group1-title',
        },
        options: ['monthly', 'annual'].map(cycle => {
          return {
              value: cycle,
              title: this.$t(`billing.billing_cycle.${cycle}`),
              subTitle: this.$money(this.costMap[cycle], { forceDiff: true }),
              description: cycle === 'monthly' ? this.$t('billing.monthly_cost') : this.$t('billing.annual_cost'),
              showSave: BILLING_CYCLE.ANNUAL === cycle,
              ...this.costMap,
              options: [...payAsYouGo, ...(cycle === 'monthly' ? monthly : annual)].map(plan => {
                return {
                  value: plan.plan_code,
                  title: this.getCallingFeesText(plan).title,
                  subTitle: this.$money(plan.amount, { forceDiff: true }),
                  description: this.getCallingFeesText(plan).unit,
                }
              })
            }
        })
      }
    },
    planAndCycleTrackingData() {
      return [{ buyInteraction: 'zoom phone subscription cycle', }, { buyInteraction: 'zoom phone international calling fees', },];
    },
    allPbxPlans() {
      return computedPbxPlans(this.planMapFlat);
    },
    selectedBasePlan() {
      return computedPbxBasePlans(this.getSelectedPlans)[0];
    },
    selectedCallingPlan() {
      return computedPbxCallingPlans(this.getSelectedPlans)[0];
    },
    selectedTFPlan() {
      return computedPbxPlans(this.getSelectedPlans).find(item => item.sub_type === PBX_SUB_TYPES.TOLLFREE);
    },
    defaultTFPlan() {
      return this.allPbxPlans.find(item => (
        item.region === PBX_REGIONS.JP &&
        item.billing_cycle === this.pbxBillingCycle &&
        item.sub_type === PBX_SUB_TYPES.TOLLFREE
      ));
    },
    costMap() {
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => (
        item.sub_type === PBX_SUB_TYPES.UNLIMITED ||
        item.sub_type === PBX_SUB_TYPES.METERED ||
        item.sub_type === PBX_SUB_TYPES.TOLLFREE ||
        item.sub_type === PBX_SUB_TYPES.PHONE_NUMBER
      ))
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const monthlyPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.MONTHLY);
      const annualPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.ANNUAL);
      const _costMap = {
        [BILLING_CYCLE.MONTHLY]: monthlyPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
        [BILLING_CYCLE.ANNUAL]: annualPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
      };
      return _costMap;
    },
    tollFreeMax() {
      return 99 - (this.totalPhoneNumbers - this.form.tollFreeNumber);
    },
    numberTips() {
      if (this.totalPhoneNumbers >= 99) {
        return {
          text: this.$t('billing.pbx.more_than_99_license', [this.pbxContactSalesLink]),
          type: 'warning',
        };
      }
      return null;
    },
    callingFeeLabel() {
      if(!this.selectedCallingPlan) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.UNLIMITED) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED) return this.$t('billing.pbx.calling_fee_label.metered');
      return this.$t('billing.pbx.calling_fee_label.unlimited');
    },
    callingFeeName() {
      if(!this.selectedBasePlan) return '';
      return this.$t('billing.pbx.metered_calling_credits');
    },
    selectedPbxBillingCycle() {
      const selectedCallingPlans = computedPbxCallingPlans(this.selectedPbxPlans);
      return selectedCallingPlans?.[0]?.billing_cycle; // HACK implicit dependency that calling plans are same on billing_cycle
    },
    callingPlansDesc() {
      return this.$t(pbxGlobalSelect.find(r => r.key === this.activeRegionKey).desc);
    },
    regionsList() {
      return [ { key: 'GS', label: 'billing.pbx.global_select', desc: 'billing.pbx.calling_plans_Global_Select' }];
    },
    callingFeesLabel() {
      const price = this.$money(this.selectedBasePlan?.amount, { forceDiff: true })
      const callingFees = this.getCallingFeesText(this.selectedBasePlan);
      return `${this.$t('billing.pbx.calling_fee_name.pay_you_go.UC')}: ${callingFees?.title} • ${price}${callingFees?.unit}`
    },
    // display name of currently selected region label
    activeRegionLabel() {
      return this.getRegionLabelById(this.activeRegionKey)
    },
  },
  methods: {
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
      setPbxBasePlanCode: 'setPbxBasePlanCode',
      setPbxBillingCycle: 'setPbxBillingCycle',
    }),
    getCallingFeesText: getZPCallingFeesText,
    addAnotherCallingPlan() {
      this.$refs.callingSelect.newItem();
      trackingAddAnotherCallingPlan(this.getRegionLabelById(this.activeRegionKey));
    },
    addAdditionalNumbers() {
      this.$refs.tollfreeSelect.newItem();
      trackingAddAdditionalNumbers(this.getRegionLabelById(this.activeRegionKey));
    },
    onRadioChange(val) {
      trackingSelectCallingPlan(this.getRegionLabelById(val));
    },
    updateCallingPlan(planCode,number) {
      this.updateSelectPlan({
        plan_code: planCode,
        cartInfo: { selected_number: number  },
      });
    },

    async initPbxForm() {
      if(this.selectedBasePlan) {
        this.pbxBasePlanCode = this.selectedBasePlan.plan_code;
      }
      if(this.selectedCallingPlan) {
        this.pbxBillingCycle = this.selectedCallingPlan.billing_cycle;
        this.form.category = this.selectedCallingPlan.category;
      } else if(!this.selectedBasePlan.is_payg) {
        this.pbxBillingCycle = this.selectedBasePlan.billing_cycle;
      } else {
        this.pbxBillingCycle = BILLING_CYCLE.ANNUAL;
      }

      if(this.selectedTFPlan) {
        this.form.tollFreeNumber = Number(this.selectedTFPlan.cartInfo.selected_number);
      }
      this.fillSelectModel(this.regionsConfig);
    },

    handleBillingCycleChange(value) {
      this.selectedPbxPlans
      .filter(item => item.sub_type === PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => item.sub_type !== PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      this.switchPlansBillingCycle(selectedCategoryNumberMap, value);
    },
    switchPlansBillingCycle(categoryNumberMap, billingCycle) {
      this.allPbxPlans
        // get selected plans
        .filter(item => categoryNumberMap[item.category])
        // remove selected plans
        .forEach(item => this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: 0 } }))
        // get plans by billing cycle

      this.allPbxPlans
        .filter(item => categoryNumberMap[item.category] && item.billing_cycle === billingCycle)
        // add plan to cart
        .forEach(item => {
          if(categoryNumberMap[item.category]) {
            this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: categoryNumberMap[item.category] } })
            categoryNumberMap[item.category] = 0;
          }
        });
      const overwriteMerge = (destinationArray, sourceArray) => sourceArray
      this.regionsConfig = merge(this.regionsConfig,this.fillSelectModel(),{ arrayMerge: overwriteMerge })
    },
    switchPlansCycle(billingCycleInfo) {
      this.handleBillingCycleChange(billingCycleInfo[0])
      this.handleBasePlanChange(billingCycleInfo[1])
    },
    handleBasePlanChange(value) {
      this.updateSelectPlan({
        plan_code: this.selectedBasePlan.plan_code,
        cartInfo: { selected_number: 0 },
      });
      this.updateSelectPlan({
        plan_code: value,
        cartInfo: { selected_number: 1 },
      });
    },
    updateCallingPlansToCart(plans){
      !!plans && plans.map(c => this.updateCallingPlan(c.id,c.number))
    },
    getCallingPlanLabelForSelect(region) {
      return this.$t(region.label) + '<div style="visibility: hidden;width:0;height:0;">include</div>';
    },
    fillSelectModel(thisRegionsConfig = {}) {
      // ignoring thisRegionsConfig will return a plain object to init data.regionsConfig
      const _map = (plans) =>
        plans.map((x) => ({
          id: x.plan_code,
          number: x.cartInfo?.selected_number ?? 0,
        }));
      pbxGlobalSelect.forEach((region) => {
        const plans = _.chain(this.boughtPbxPlans || [])
          .map((p) => (this.getSelectedPlansMap[p.plan_code] || p)) // selected plans overwrite bought plans
          .concat(this.getSelectedPlans || [])
          .filter((x) => (PBX_GS.includes(x.category)))
          .uniqBy((p) => p.plan_code)
          .value();

        const callingPlans = computedPbxCallingPlans(plans);
        const additionalPlans = [...computedPbxPhoneNumberPlans(plans), ...computedPbxTollFreePlans(plans)];
        _set(thisRegionsConfig, `${region.key}.callings`, _map(callingPlans));
        _set(thisRegionsConfig, `${region.key}.additions`, _map(additionalPlans));
      });
      return thisRegionsConfig;
    },
    testRegionalCallingPlansFull(billingCycle) {
      const plans = this.globalSelectCallingPlans.filter(x => x.billing_cycle === billingCycle);
      return this.regionsConfig[this.activeRegionKey]?.callings?.length === plans.length;
    },
    testRegionalAdditionalPlansFull(billingCycle) {
      const plans = this.globalSelectAdditionalPlans.filter(x => x.billing_cycle === billingCycle);
      return this.regionsConfig[this.activeRegionKey]?.additions?.length === plans.length;
    },

  },
  watch: {
    selectedTFPlan(value) {
      if(!value) {
        this.form.tollFreeNumber = 0;
      }
    },
    callingPlanToolTip(v) {
      v && this.$tracking.buyInteraction('zoom phone options', 'What calling plans do your users need? - tooltip');
    },
    callingfeesToolTip(v) {
      v && this.$tracking.buyInteraction('zoom phone options', 'Choose how you’d like to pay - tooltip');
    },
    additionalNumbersToolTip(v) {
      v && this.$tracking.buyInteraction('zoom phone options', 'Add additional numbers - tooltip');
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `zoom phone - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-pbx,.opc-pbx-regional {

  .zm-input-number{
    @include media(mobile,tablet){
      margin: 10px 0;
    }
  }
  &-head{
    display: flex;
    font-weight: 600;
    font-size: 18px;
    align-items: center;

    @include media(mobile,tablet){
      align-items: flex-start;
    }

    &__logo{
      min-height: 32px
    }
    .opc-pbx__link{
      padding: 0;
    }
  }
  &__logo {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
    margin-right: 12px;
    &-icon {
      display: block;
      width: auto;
      height: 32px;
      margin-right: 12px;
    }
  }
  &__checked-icon {
    margin-left: 8px;
    &:before {
      color: #0B5CFF;
    }
  }
  .main-icon {
    color: $theme-color;
  }
  .opc-tag-radio{
    margin-bottom: 20px;
  }
  .zm-button--link {
    color: $theme-color;
  }
  .label-title-1 {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 150%;
      color: #00031F;
      margin-bottom: 20px;
      @include media(mobile,tablet){
        margin-bottom: 10px;
      }
  }
  .opc-card-cascade{
    margin-top: 20px;
  }
  &__name {
    font-family: $happy-display;
    font-style: normal;
    font-weight: 400;
    font-size: 32px;
    line-height: 150%;
    color: #00053D;
    margin-bottom: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
    .zm-icon-success {
      font-size: 16px;
      margin-left: 12px;
    }
  }
  &__link {
    padding: 0;
    margin: 0;
    color: $theme-color;
  }
  &__main {
    margin-top: 24px;
    border-top: 1px solid #DFDFE4;
    padding-top: 24px;
    @include media(mobile,tablet){
    margin-top: 10px;
    padding-top: 10px;
  }
  }
  .opration-loading {
    height: 24px;
    width: 100%;
    .zm-loading-spinner {
      margin-top: -12px;
      height: 24px;
      .circular {
        width: 24px;
        height: 24px;
      }
    }
  }

  .main-color-icon {
    color: $theme-color;
  }
  .toll-free-label {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 120%;
    color: $theme-color;
    padding: 1px 4px;
    background: rgba(0, 5, 61, 0.08);
    border-radius: 4px;
    margin-left: 8px;
  }
  .zm-tag--middle.number-tag {
    padding: 8px 10px;
    margin-right: 8px;
    margin-bottom: 8px;
    .zm-tag__label {
      padding: 0;
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      color: #00053D;
    }
  }

  .billing-icon-warning {
    width: 16px;
    height: 16px;
    position: relative;
    &::before {
      content: '';
      background-image: url('../../../assets/Alert.svg');
      width: 16px;
      height: 16px;
      background-repeat: no-repeat;
      background-size: cover;
      position: absolute;
      top: 0;
      left: 0;
    }
  }
  .zm-alert {
    margin-bottom: 8px;
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    .number-tips-info {
      display: flex;
      align-items: center;
      p {
        margin-left: 13px;
      }
    }
    .zm-icons.zm-icons--mini {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
    &--warning {
      color: #815A30;
    }

    &--info {
      color: #515079;
    }
  }
  .opc-pbx__desc {
    ul, li {
      list-style: none;
    }
    li {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #00053D;
    }
  }
}
.opc-config {
  &__collapse {
    font-size: 14px;
    line-height: 21px;
    margin-top: 12px;
    font-weight: 500;
  }
}
</style>
